import {
  sourceByName,
  type PresentationKind,
  type ReportLayoutWidget,
  type ReportSource,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { ReportsViewWidgetQuestion } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetQuestion";
import { AiRewrite } from "@/components/organisms/AiRewrite/AiRewrite";

/** What came back: a widget, not yet anybody's. */
type Draft = {
  source: string;
  presentation: PresentationKind;
  spec: Record<string, unknown>;
  title: string;
};

/**
 * A card described rather than assembled.
 *
 * The sentence in the header is quick to change one word of and slow to write
 * from nothing: eight menus stand between an empty card and "total amount of
 * transactions per category where the amount is over a hundred". So the card
 * can be described instead - and what comes back is previewed as the sentence
 * it would make, drawn by what draws it on the card, with its words still
 * live, so a draft that is nearly right can be finished by hand.
 */
export function ReportsViewWidgetPrompt({
  widget,
  sources,
  onApply,
}: {
  widget: ReportLayoutWidget;
  /** Everything the ledger can be asked about, for drawing the sentence. */
  sources: readonly ReportSource[];
  onApply: (patch: Partial<ReportLayoutWidget>) => void;
}) {
  return (
    <AiRewrite<Draft>
      kind="report_widget"
      label="Describe this widget"
      placeholder="What should this card show? e.g. what I spent per contact this year, over a hundred dollars"
      current={{
        source: widget.source ?? null,
        presentation: widget.presentation ?? null,
        spec: widget.spec ?? {},
      }}
      preview={(draft, change) => (
        <ReportsViewWidgetQuestion
          source={sourceByName(draft.source, sources)}
          sources={sources}
          spec={draft.spec}
          presentation={draft.presentation}
          onChange={(patch) =>
            change({
              ...draft,
              ...(patch.source ? { source: patch.source } : {}),
              ...(patch.presentation ? { presentation: patch.presentation } : {}),
              ...(patch.spec ? { spec: patch.spec } : {}),
            })
          }
        />
      )}
      onApply={(draft) =>
        onApply({
          source: draft.source,
          presentation: draft.presentation,
          spec: draft.spec,
          ...(draft.title ? { title: draft.title } : {}),
        })
      }
    />
  );
}
